import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { carrierNav } from "@/pages/carrier/nav";
import { Metric } from "@/components/common";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Search, Package, DollarSign, Gauge, FileText, Bot, ShieldCheck, ShieldAlert, Repeat, Truck, ArrowRight, Loader2, AlertTriangle, Clock } from "lucide-react";

export default function CarrierDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);

  useEffect(() => { api.get("/carrier/dashboard").then((r) => setStats(r.data)).catch(() => toast.error("Failed to load dashboard")); }, []);
  useEffect(() => { api.get("/carrier/document-alerts").then((r) => setAlerts(r.data)).catch(() => {}); }, []);

  if (!stats) return <DashboardShell nav={carrierNav}><div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-orange" size={32} /></div></DashboardShell>;

  const verified = stats.verified;
  const activeAlerts = (alerts?.alerts || []).filter((a) => a.status !== "valid");
  return (
    <DashboardShell nav={carrierNav}>
      {/* Verification banner */}
      <div className={`rounded-xl border p-5 mb-6 flex items-center gap-4 ${verified ? "border-xd-green/40 bg-xd-green/10" : "border-xd-yellow/40 bg-xd-yellow/10"}`}>
        {verified ? <ShieldCheck className="text-xd-green shrink-0" size={28} /> : <ShieldAlert className="text-xd-yellow shrink-0" size={28} />}
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold">{verified ? "XD VERIFIED CARRIER" : "Verification " + (stats.verification_status || "pending")}</p>
          <p className="text-sm text-xd-muted">{verified ? "You can book loads and access all carrier tools." : "Complete onboarding & get admin-approved to unlock booking."}</p>
        </div>
        {!verified && <Button data-testid="go-onboarding-btn" onClick={() => nav("/onboarding")} className="bg-xd-orange hover:bg-xd-orangeH text-white shrink-0">Continue Onboarding</Button>}
      </div>

      {/* Document expiration alerts */}
      {activeAlerts.length > 0 && (
        <div data-testid="doc-alerts-banner" className="rounded-xl border border-xd-red/40 bg-xd-red/10 p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="text-xd-red" size={20} />
            <p className="font-heading font-bold">Compliance alerts</p>
          </div>
          <div className="space-y-2">
            {activeAlerts.map((a, i) => (
              <div key={i} data-testid={`doc-alert-${i}`} className="flex items-center justify-between gap-3 text-sm bg-xd-paper border border-xd-border rounded-lg px-4 py-2.5">
                <span className="min-w-0 truncate"><span className="font-semibold">{a.category}</span> · {a.label}</span>
                {a.status === "expired"
                  ? <span className="inline-flex items-center gap-1 text-xs font-bold text-xd-red shrink-0"><AlertTriangle size={12} /> Expired {Math.abs(a.days_left)}d ago</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-bold text-xd-yellow shrink-0"><Clock size={12} /> Expires in {a.days_left}d</span>}
              </div>
            ))}
          </div>
          <Button data-testid="alerts-manage-btn" onClick={() => nav("/dashboard/documents")} variant="outline" className="mt-3 border-xd-border text-white hover:bg-xd-surface">Manage documents</Button>
        </div>
      )}

      <h1 className="font-heading text-3xl font-black tracking-tight mb-4">Welcome back</h1>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Metric testid="metric-available" label="Available Loads" value={stats.available_loads} accent="orange" />
        <Metric testid="metric-booked" label="Booked Loads" value={stats.booked_loads} accent="white" />
        <Metric testid="metric-revenue" label="Revenue / Mo" value={`$${stats.revenue_month?.toLocaleString()}`} accent="green" />
        <Metric testid="metric-rpm" label="Avg $/Mile" value={`$${stats.avg_rate_mile}`} accent="blue" />
        <Metric testid="metric-outstanding" label="Outstanding" value={`$${stats.outstanding_payments?.toLocaleString()}`} accent="white" />
        <Metric testid="metric-completion" label="Profile" value={`${stats.profile_completion}%`} accent="orange" />
      </div>

      <div className="mt-4 bg-xd-paper border border-xd-border rounded-xl p-5">
        <div className="flex justify-between text-sm mb-2"><span className="font-medium">Profile completion</span><span className="text-xd-orange font-bold">{stats.profile_completion}%</span></div>
        <Progress value={stats.profile_completion} className="h-2 bg-xd-border" />
      </div>

      {/* Feature grid */}
      <h2 className="mt-8 font-heading text-xl font-bold mb-4">Your tools</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tool to="/find-loads" icon={Search} title="Find Loads" desc="Browse the hotshot marketplace" />
        <Tool to="/dashboard/ai-dispatcher" icon={Bot} title="Smart Recommendations" desc="Loads ranked for your business" />
        <Tool to="/dashboard/my-loads" icon={Package} title="My Loads" desc="Booked & in-transit loads" />
        <Tool to="/dashboard/money" icon={DollarSign} title="Revenue & Payments" desc="Revenue, expenses & profit" />
        <Tool to="/dashboard/score" icon={Gauge} title="XD Score" desc="Platform-readiness score" />
        <Tool to="/dashboard/documents" icon={FileText} title="Documents" desc="W-9, COI, BOL, POD vault" />
        <Tool to="/marketplace" icon={Truck} title="Marketplace" desc="Fuel, factoring & partners" />
        <Tool to="/funding" icon={Repeat} title="Funding" desc="Capital & equipment financing" />
      </div>
    </DashboardShell>
  );
}

function Tool({ to, icon: Icon, title, desc }) {
  return (
    <Link to={to} data-testid={`tool-${title.toLowerCase().replace(/\s+/g, "-")}`} className="group bg-xd-paper border border-xd-border rounded-xl p-5 hover:border-xd-orange/50 transition-colors">
      <span className="grid place-items-center w-10 h-10 rounded-lg bg-xd-orange/15 text-xd-orange"><Icon size={20} /></span>
      <h3 className="mt-3 font-heading font-bold">{title}</h3>
      <p className="text-xs text-xd-muted">{desc}</p>
      <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-xd-orange group-hover:gap-2 transition-all">Open <ArrowRight size={13} /></span>
    </Link>
  );
}
